import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/toaster';
import { Target, Heart, Lightbulb, Award, Rocket, Users, Linkedin } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Mission-Driven',
      description: 'Empower businesses worldwide to achieve measurable growth through intelligent lead management.',
    },
    {
      icon: Lightbulb,
      title: 'Innovation First',
      description: 'Push the boundaries of what\'s possible with AI and automation in B2B marketing.',
    },
    {
      icon: Heart,
      title: 'Customer Obsessed',
      description: 'Your success is our success. We provide world-class support every step of the way.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Deliver exceptional quality in our product, service, and customer experience.',
    },
  ];

  const team = [
    { 
      name: 'Anuvinder Singh', 
      role: 'CEO', 
      image: 'https://cdn.prod.website-files.com/6808e4f17c26e6f68bc92a53/685c136a8fa13aa0fadc4d50_anu-4.png',
      linkedin: 'https://www.linkedin.com/in/anuvinders'
    },
    { 
      name: 'Pravin Patne', 
      role: 'IT Manager', 
      image: 'https://cdn.prod.website-files.com/6808e4f17c26e6f68bc92a53/686778b87d6b1a9d1ecea9c2_pp2.png',
      linkedin: 'https://www.linkedin.com/in/pravin-patne-50546559'
    },
    { 
      name: 'Vishal Merekar', 
      role: 'Senior Developer', 
      image: 'https://cdn.prod.website-files.com/6808e4f17c26e6f68bc92a53/6866988731fb0fe0a1bcf742_vishal-8%20(1).png',
      linkedin: 'https://www.linkedin.com/in/vishal-merekar'
    },
    { 
      name: 'Ashish Kakde', 
      role: 'Developer', 
      image: 'https://cdn.prod.website-files.com/6808e4f17c26e6f68bc92a53/686794d1c2df6dc7c9626193_kka.png',
      linkedin: 'https://www.linkedin.com/in/ashish-kakde-26b85a257'
    },
    { 
      name: 'Kiran Thorat', 
      role: 'Developer', 
      image: 'https://cdn.prod.website-files.com/6808e4f17c26e6f68bc92a53/685cdbebacfc607e9a3c8ed9_k5.png',
      linkedin: 'https://www.linkedin.com/in/kiran-thorat-a57068247'
    },
  ];

  const milestones = [
    { year: '2020', event: 'Company Founded', description: 'AALIGN was born in San Francisco' },
    { year: '2021', event: '1,000 Customers', description: 'Reached our first major milestone' },
    { year: '2022', event: 'Series A Funding', description: '$15M raised to accelerate growth' },
    { year: '2023', event: 'AI Launch', description: 'Launched industry-first AI features' },
    { year: '2024', event: '10K Customers', description: 'Serving businesses worldwide' },
    { year: '2025', event: 'Global Expansion', description: 'Opened offices in Europe and Asia' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Toaster />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 px-5 py-2.5 rounded-full text-sm font-bold mb-8">
              <Users className="w-4 h-4" />
              About AALIGN
            </div>
            <h1 className="text-5xl lg:text-7xl font-black mb-8">
              Building the Future of
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
                Lead Management
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're on a mission to help businesses around the world to capture, nurture, and convert more leads using AI-powered technology.
            </p>
          </div>
          <div className="relative max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80"
              alt="Team"
              className="w-full"
            />
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-black mb-6">
                Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">Story</span>
              </h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  Founded in 2020 by a team of former marketing executives and engineers, AALIGN was born from a simple frustration: existing lead management tools were too complex, too expensive, or simply didn't work.
                </p>
                <p>
                  We set out to build something better—a platform that combines the power of artificial intelligence with intuitive design to help businesses of all sizes capture and convert more leads.
                </p>
                <p>
                  Today, we're proud to serve over 300 businesses worldwide, processing millions of leads and helping our customers drive real revenue growth.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-200 to-red-200 rounded-3xl blur-3xl opacity-30"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-12 border-2 border-gray-100">
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <div className="text-5xl font-black text-orange-600 mb-2">1K+</div>
                    <div className="text-gray-600 font-semibold">Users Worldwide</div>
                  </div>
                  <div>
                    <div className="text-5xl font-black text-orange-600 mb-2">95%</div>
                    <div className="text-gray-600 font-semibold">Customer Satisfaction</div>
                  </div>
                  <div>
                    <div className="text-5xl font-black text-orange-600 mb-2">10+</div>
                    <div className="text-gray-600 font-semibold">Years of Innovation</div>
                  </div>
                  <div>
                    <div className="text-5xl font-black text-orange-600 mb-2">$10M</div>
                    <div className="text-gray-600 font-semibold">Efficient Financial</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">Key milestones that shaped AALIGN</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {milestones.map((milestone, idx) => (
              <div key={idx} className="relative p-8 bg-gradient-to-br from-orange-500 to-red-600 text-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow">
                <div className="text-6xl font-black mb-4 opacity-90">{milestone.year}</div>
                <h3 className="text-2xl font-bold mb-2">{milestone.event}</h3>
                <p className="text-orange-100">{milestone.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600">The people behind AALIGN</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, idx) => (
              <div key={idx} className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-1">{member.name}</h3>
                  <p className="text-orange-600 font-semibold mb-3">{member.role}</p>
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span className="text-sm font-medium">Connect on LinkedIn</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-orange-500 to-red-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-black mb-6">
            Join Us on Our Mission
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            We're always looking for talented people to join our team.
          </p>
          <Link to="/careers" className="bg-white text-orange-600 hover:bg-gray-100 font-bold px-12 py-4 rounded-xl shadow-2xl transition-all transform hover:scale-105 inline-block">
            View Open Positions
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;